/**
 * MandateDecisionRecord — The legitimacy instrument.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ScrollText, ExternalLink } from 'lucide-react';
import { useMandateStore } from '../../stores/mandate/mandateStore';
import { useProductSurface } from '../../hooks/useProductSurface';
import { PageHeader } from '../../components/prioritization/PageHeader';
import type { MandateDecision } from '../../types/mandate';
import type { AddDecisionInput, UpdateDecisionInput } from '../../stores/mandate/mandateStore';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface DecisionFormState {
  title: string;
  description: string;
  rationale: string;
  signal: string;
  peopleConsulted: string;
  outcome: string;
}

const EMPTY_FORM: DecisionFormState = {
  title: '',
  description: '',
  rationale: '',
  signal: '',
  peopleConsulted: '',
  outcome: '',
};

function decisionToForm(d: MandateDecision): DecisionFormState {
  return {
    title: d.title,
    description: d.description ?? '',
    rationale: d.rationale,
    signal: d.signal ?? '',
    peopleConsulted: d.peopleConsulted ?? '',
    outcome: d.outcome ?? '',
  };
}

export default function MandateDecisionRecord() {
  const { decisions, addDecision, updateDecision, deleteDecision } = useMandateStore();
  const { to } = useProductSurface();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<DecisionFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  function openAdd() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  }

  function openEdit(d: MandateDecision) {
    setForm(decisionToForm(d));
    setEditingId(d.id);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function setField(field: keyof DecisionFormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!form.title.trim() || !form.rationale.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        const updates: UpdateDecisionInput = {
          title: form.title.trim(),
          description: form.description.trim() || null,
          rationale: form.rationale.trim(),
          signal: form.signal.trim() || null,
          peopleConsulted: form.peopleConsulted.trim() || null,
          outcome: form.outcome.trim() || null,
        };
        await updateDecision(editingId, updates);
      } else {
        const data: AddDecisionInput = {
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          rationale: form.rationale.trim(),
          signal: form.signal.trim() || undefined,
          peopleConsulted: form.peopleConsulted.trim() || undefined,
          outcome: form.outcome.trim() || undefined,
        };
        await addDecision(data);
      }
      closeForm();
    } finally {
      setSaving(false);
    }
  }

  const consultedTags = (d: MandateDecision) =>
    d.peopleConsulted
      ? d.peopleConsulted.split(',').map((s) => s.trim()).filter(Boolean)
      : [];

  return (
    <div>
      <PageHeader
        icon={<ScrollText className="h-4 w-4 text-[var(--mustard)]" />}
        iconBg="bg-[var(--mustard-dim)]"
        title="Decision Record"
        subtitle="What you've chosen, why, and what changed"
        actions={
          <button
            onClick={openAdd}
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 text-sm"
          >
            + Record a decision
          </button>
        }
      />

      {/* Explanation bar */}
      <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 mb-6">
        <p className="text-sm text-[var(--mustard)]">
          These are the consequential choices you've made since Day 1. At Day 100, this record
          answers: what is different now than when you started, and why?
        </p>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
          <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-4">
            {editingId ? 'Edit decision' : 'Record a decision'}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                Title <span className="text-[var(--coral)]">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Decision title"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                Description <span className="text-[var(--text-ter)] font-normal">(optional)</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                rows={2}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                Why did you make this choice? <span className="text-[var(--coral)]">*</span>
              </label>
              <textarea
                value={form.rationale}
                onChange={(e) => setField('rationale', e.target.value)}
                rows={3}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="Rationale for this choice..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                What changed / what information drove this?{' '}
                <span className="text-[var(--text-ter)] font-normal">(optional)</span>
              </label>
              <p className="text-xs text-[var(--text-ter)] mb-1">
                Leave blank if this was a standing decision.
              </p>
              <textarea
                value={form.signal}
                onChange={(e) => setField('signal', e.target.value)}
                rows={2}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="Signal or new information..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                People consulted <span className="text-[var(--text-ter)] font-normal">(optional, comma-separated)</span>
              </label>
              <input
                type="text"
                value={form.peopleConsulted}
                onChange={(e) => setField('peopleConsulted', e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g. Sarah, Mark, CTO"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
                What happened?{' '}
                <span className="text-[var(--text-ter)] font-normal">(outcome — fill in later)</span>
              </label>
              <textarea
                value={form.outcome}
                onChange={(e) => setField('outcome', e.target.value)}
                rows={2}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="Leave blank for now..."
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={handleSave}
              disabled={!form.title.trim() || !form.rationale.trim() || saving}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium px-4 py-2 text-sm"
            >
              {saving
                ? 'Saving...'
                : editingId
                ? 'Update'
                : 'Record decision'}
            </button>
            <button
              onClick={closeForm}
              className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium px-4 py-2 border border-[var(--rule)] hover:bg-[var(--surface)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Decision list */}
      {decisions.length === 0 ? (
        <p className="text-sm text-[var(--text-ter)] text-center py-8">
          No decisions logged yet. Log your first decision above.
        </p>
      ) : (
        <div className="space-y-3">
          {decisions.map((d) => {
            const tags = consultedTags(d);
            return (
              <div key={d.id} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 group">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--text-pri)]">{d.title}</p>
                    {d.description && (
                      <p className="text-sm text-[var(--text-sec)] mt-1">{d.description}</p>
                    )}

                    {/* Rationale */}
                    <div className="bg-[var(--surface)] p-3 mt-2">
                      <span className="text-xs font-semibold text-[var(--text-ter)]">Rationale: </span>
                      <span className="text-sm text-[var(--text-sec)]">{d.rationale}</span>
                    </div>

                    {/* Signal */}
                    {d.signal && (
                      <div className="bg-[var(--mustard-dim)] border-l-4 border-[var(--mustard)] p-3 mt-2">
                        <span className="text-xs font-semibold text-[var(--mustard)]">
                          Signal — what changed:{' '}
                        </span>
                        <span className="text-sm text-[var(--mustard)]">{d.signal}</span>
                      </div>
                    )}

                    {/* People consulted */}
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {tags.map((t) => (
                          <span
                            key={t}
                            className="text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] px-2 py-0.5 rounded"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Outcome */}
                    {d.outcome && (
                      <p className="text-sm text-[var(--text-sec)] italic mt-2">{d.outcome}</p>
                    )}

                    <div className="flex items-center gap-3 mt-3">
                      <p className="text-xs text-[var(--text-ter)]">{formatDate(d.createdAt)}</p>
                      {d.platformDecisionId && (
                        <Link
                          to={to(`/platform/decisions?highlight=${d.platformDecisionId}`)}
                          className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                        >
                          In Decisions <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Edit / Delete */}
                  <div className="flex items-center gap-1 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEdit(d)}
                      className="text-xs text-[var(--text-ter)] hover:text-primary-600 font-medium px-2 py-1 rounded hover:bg-primary-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteDecision(d.id)}
                      className="text-xs text-[var(--text-ter)] hover:text-[var(--coral)] font-medium px-2 py-1 rounded hover:bg-[var(--coral-dim)]"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
